/**
 * Persistence layer for SearchScreen — recent queries + saved filters.
 *
 * Stored in localStorage so the user keeps their list across reloads. The
 * shape lives outside SearchScreen so we can evolve the storage format
 * without rewiring component state, and so unit tests can cover it
 * without rendering React.
 *
 * Sharp edges:
 *   - We tolerate corrupt JSON. A malformed payload returns the empty list
 *     and silently overwrites on the next write — wedging the search
 *     screen because of one bad localStorage entry would be hostile.
 *   - Reads/writes are wrapped in try/catch since localStorage throws in
 *     incognito Safari and on quota-exceeded.
 *   - Recent queries are deduped case-insensitively but stored verbatim
 *     (so "電費" replaces "電費" but "Cafe" replaces "cafe").
 */

import type { KindFilter, DateRangeKey } from '@/components/transactions/searchFilters';

const RECENT_KEY = 'mochia.search.recent.v1';
const SAVED_KEY = 'mochia.search.saved.v1';
const RECENT_MAX = 8;

export interface AmountFilter {
  op: '>' | '<' | '>=' | '<=' | '=';
  /** Major-unit amount typed by the user. Compared against
   *  `convertMinor(t.amount_minor, t.currency, primary) / 100`. */
  amount: number;
}

export interface PersistableFilters {
  query: string;
  kind: KindFilter;
  categoryId: string | null;
  bookId: string | null;
  dateRange: DateRangeKey | null;
  amount: AmountFilter | null;
}

export interface SavedFilter {
  id: string;
  name: string;
  filters: PersistableFilters;
  createdAt: string; // ISO 8601
}

function safeParse<T>(raw: string | null, fallback: T): T {
  if (!raw) return fallback;
  try {
    const parsed = JSON.parse(raw);
    return parsed as T;
  } catch {
    return fallback;
  }
}

function safeRead(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeWrite(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore quota / incognito errors — persistence is best-effort */
  }
}

export function loadRecentSearches(): string[] {
  const arr = safeParse<unknown>(safeRead(RECENT_KEY), []);
  if (!Array.isArray(arr)) return [];
  return arr.filter((x): x is string => typeof x === 'string').slice(0, RECENT_MAX);
}

export function pushRecentSearch(q: string): string[] {
  const trimmed = q.trim();
  if (!trimmed) return loadRecentSearches();
  const lower = trimmed.toLowerCase();
  const existing = loadRecentSearches().filter(
    (s) => s.toLowerCase() !== lower,
  );
  const next = [trimmed, ...existing].slice(0, RECENT_MAX);
  safeWrite(RECENT_KEY, JSON.stringify(next));
  return next;
}

export function clearRecentSearches(): void {
  safeWrite(RECENT_KEY, JSON.stringify([]));
}

export function loadSavedFilters(): SavedFilter[] {
  const arr = safeParse<unknown>(safeRead(SAVED_KEY), []);
  if (!Array.isArray(arr)) return [];
  return arr.filter((x): x is SavedFilter => {
    if (!x || typeof x !== 'object') return false;
    const o = x as Record<string, unknown>;
    return (
      typeof o.id === 'string' &&
      typeof o.name === 'string' &&
      typeof o.filters === 'object' &&
      o.filters != null
    );
  });
}

export function saveFilter(name: string, filters: PersistableFilters): SavedFilter {
  const trimmed = name.trim();
  if (!trimmed) throw new Error('filter name is required');
  const id =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `sf_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const entry: SavedFilter = {
    id,
    name: trimmed,
    filters,
    createdAt: new Date().toISOString(),
  };
  const next = [entry, ...loadSavedFilters()];
  safeWrite(SAVED_KEY, JSON.stringify(next));
  return entry;
}

export function deleteSavedFilter(id: string): SavedFilter[] {
  const next = loadSavedFilters().filter((f) => f.id !== id);
  safeWrite(SAVED_KEY, JSON.stringify(next));
  return next;
}

/** Test-only — wipe everything this module owns. */
export function _resetForTests(): void {
  safeWrite(RECENT_KEY, JSON.stringify([]));
  safeWrite(SAVED_KEY, JSON.stringify([]));
}
